import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Shield, Users, Building2, User, Briefcase, MessageSquare, 
  TrendingUp, Download, LogOut, RefreshCw, AlertTriangle,
  CheckCircle, Clock, BarChart3, Calendar, Filter, Eye, List, 
  ChevronRight, MapPin, Tag, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Search, Star, Send, Trash2, Link2,
  Activity, Monitor, Smartphone, Tablet, Globe, Headphones
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sort icon component - defined outside to avoid recreating on each render
const SortIcon = ({ column, currentSort, currentDir }) => {
  if (currentSort !== column) {
    return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  }
  return currentDir === 'asc' 
    ? <ArrowUp className="w-3 h-3 ml-1 text-[#344B71]" />
    : <ArrowDown className="w-3 h-3 ml-1 text-[#344B71]" />;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [exportType, setExportType] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Listings state
  const [listings, setListings] = useState([]);
  const [listingsFilter, setListingsFilter] = useState({ status: 'all', priority: 'all', search: '' });
  const [listingsSortBy, setListingsSortBy] = useState('created_at');
  const [listingsSortDir, setListingsSortDir] = useState('desc');
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  
  // Reviews state
  const [reviewsData, setReviewsData] = useState({ statistics: null, reviews: [] });
  
  // Verifications state
  const [professionals, setProfessionals] = useState([]);
  const [verificationFilter, setVerificationFilter] = useState('all'); // all, verified, unverified
  const [verificationSearch, setVerificationSearch] = useState('');
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [verificationNotes, setVerificationNotes] = useState('');
  
  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesSearch, setCompaniesSearch] = useState('');
  
  // Professionals sorting state
  const [professionalsSortBy, setProfessionalsSortBy] = useState('created_at');
  const [professionalsSortDir, setProfessionalsSortDir] = useState('desc');
  
  // Companies sorting state
  const [companiesSortBy, setCompaniesSortBy] = useState('created_at');
  const [companiesSortDir, setCompaniesSortDir] = useState('desc');
  
  // Reviews sorting state
  const [reviewsSortBy, setReviewsSortBy] = useState('created_at');
  const [reviewsSortDir, setReviewsSortDir] = useState('desc');
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [userCollaborations, setUserCollaborations] = useState([]);
  const [loadingCollaborations, setLoadingCollaborations] = useState(false);
  
  // Reviews relationships state
  const [reviewsRelationships, setReviewsRelationships] = useState([]);
  
  const [conversationsSearch, setConversationsSearch] = useState('');
  const [conversationsSortBy, setConversationsSortBy] = useState('last_timestamp');
  const [conversationsSortDir, setConversationsSortDir] = useState('desc');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showMessagesDialog, setShowMessagesDialog] = useState(false);
  const [showRequestMessage, setShowRequestMessage] = useState(null);
  
  // Time filter state
  const [timePeriod, setTimePeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  
  // Visitor analytics state
  const [visitorData, setVisitorData] = useState(null);
  const [visitorDays, setVisitorDays] = useState(30);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  
  // Support chat state
  const [supportConversations, setSupportConversations] = useState([]);
  const [selectedSupportUser, setSelectedSupportUser] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [newSupportMessage, setNewSupportMessage] = useState('');
  const [sendingSupportMessage, setSendingSupportMessage] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [newChatSearch, setNewChatSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const supportMessagesEndRef = React.useRef(null);
  
  // Quick message dialog state (from professionals/companies tabs)
  const [showQuickMessageDialog, setShowQuickMessageDialog] = useState(false);
  const [quickMessageUser, setQuickMessageUser] = useState(null);
  const [quickMessageContent, setQuickMessageContent] = useState('');
  const [sendingQuickMessage, setSendingQuickMessage] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchData();
  }, [navigate]);
  
  // Refetch data when time period changes
  useEffect(() => {
    if (timePeriod !== 'custom') {
      setShowCustomDates(false);
      fetchData();
    } else {
      setShowCustomDates(true);
    }
  }, [timePeriod]);
  
  // Fetch listings when tab changes
  useEffect(() => {
    if (activeTab === 'listings') {
      fetchListings();
    } else if (activeTab === 'messages') {
      fetchConversations();
    } else if (activeTab === 'reviews') {
      fetchReviews();
    } else if (activeTab === 'visitors') {
      fetchVisitorData();
      fetchReviewsRelationships();
    } else if (activeTab === 'verifications') {
      fetchProfessionals();
    } else if (activeTab === 'professionals') {
      fetchProfessionals();
    } else if (activeTab === 'companies') {
      fetchCompanies();
    } else if (activeTab === 'support-chat') {
      fetchSupportConversations();
      fetchSupportUnreadCount();
    }
  }, [activeTab]);
  
  // Refetch visitor data when days change
  useEffect(() => {
    if (activeTab === 'visitors') {
      fetchVisitorData();
    }
  }, [visitorDays]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build stats URL with time filters
      let statsUrl = `${API}/admin/stats`;
      const params = new URLSearchParams();
      
      if (timePeriod && timePeriod !== 'all' && timePeriod !== 'custom') {
        params.append('period', timePeriod);
      }
      
      if (timePeriod === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      }
      
      if (params.toString()) {
        statsUrl += `?${params.toString()}`;
      }
      
      const [statsRes, usersRes, activitiesRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/activities`)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setActivities(activitiesRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
        toast.error('Sessione scaduta, effettua nuovamente l\'accesso');
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchListings = async () => {
    try {
      let url = `${API}/admin/listings`;
      const params = new URLSearchParams();
      if (listingsFilter.status !== 'all') params.append('status', listingsFilter.status);
      if (listingsFilter.priority !== 'all') params.append('priority', listingsFilter.priority);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url);
      setListings(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento annunci');
    }
  };
  
  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/admin/conversations`);
      setConversations(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento conversazioni');
    }
  };
  
  const viewConversationMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/admin/messages/${conversationId}`);
      setConversationMessages(res.data);
      setSelectedConversation(conversationId);
      setShowMessagesDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento messaggi');
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews`);
      setReviewsData(res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Errore nel caricamento delle recensioni');
    }
  };

  const fetchProfessionals = async () => {
    try {
      const res = await axios.get(`${API}/admin/professionals`);
      setProfessionals(res.data);
    } catch (error) {
      console.error('Error fetching professionals:', error);
      toast.error('Errore nel caricamento dei professionisti');
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/admin/companies`);
      setCompanies(res.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Errore nel caricamento delle aziende');
    }
  };

  const fetchReviewsRelationships = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews-relationships`);
      setReviewsRelationships(res.data);
    } catch (error) {
      console.error('Error fetching reviews relationships:', error);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente "${userName}"? Questa azione è irreversibile e cancellerà tutti i dati associati (profilo, annunci, messaggi, recensioni).`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`Utente ${userName} eliminato con successo`);
      // Refresh the appropriate list
      if (activeTab === 'professionals') {
        fetchProfessionals();
      } else if (activeTab === 'companies') {
        fetchCompanies();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const handleVerifyProfessional = async () => {
    if (!selectedProfessional) return;
    
    try {
      await axios.post(`${API}/admin/professionals/${selectedProfessional.id}/verify`, {
        documents: verificationDocs,
        notes: verificationNotes
      });
      toast.success('Professionista verificato con successo');
      setShowVerifyDialog(false);
      setSelectedProfessional(null);
      setVerificationDocs([]);
      setVerificationNotes('');
      fetchProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella verifica');
    }
  };

  const handleUnverifyProfessional = async (professionalId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere la verifica?')) return;
    
    try {
      await axios.post(`${API}/admin/professionals/${professionalId}/unverify`);
      toast.success('Verifica rimossa');
      fetchProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  // Fetch visitor analytics data
  const fetchVisitorData = async () => {
    setLoadingVisitors(true);
    try {
      const response = await axios.get(`${API}/analytics/admin/overview?days=${visitorDays}`);
      setVisitorData(response.data);
    } catch (error) {
      console.error('Error fetching visitor data:', error);
      toast.error('Errore nel caricamento dati visitatori');
    } finally {
      setLoadingVisitors(false);
    }
  };

  // Support Chat Functions
  const fetchSupportConversations = async () => {
    try {
      const response = await axios.get(`${API}/admin/support-chat/conversations`);
      setSupportConversations(response.data);
    } catch (error) {
      console.error('Error fetching support conversations:', error);
    }
  };

  const fetchSupportUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/support-chat/unread-count`);
      setSupportUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchSupportMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/support-chat/conversation/${userId}`);
      setSupportMessages(response.data.messages);
      setSelectedSupportUser({
        id: userId,
        ...response.data.user
      });
      // Refresh unread count
      fetchSupportUnreadCount();
      // Scroll to bottom
      setTimeout(() => {
        supportMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error fetching support messages:', error);
      toast.error('Errore nel caricamento messaggi');
    }
  };

  const sendSupportMessage = async () => {
    if (!newSupportMessage.trim() || !selectedSupportUser) return;
    
    setSendingSupportMessage(true);
    try {
      await axios.post(`${API}/admin/support-chat/send`, {
        receiver_id: selectedSupportUser.id,
        content: newSupportMessage
      });
      setNewSupportMessage('');
      fetchSupportMessages(selectedSupportUser.id);
      fetchSupportConversations();
      toast.success('Messaggio inviato');
    } catch (error) {
      console.error('Error sending support message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSendingSupportMessage(false);
    }
  };

  const startNewSupportChat = async (user) => {
    setSelectedSupportUser(user);
    setSupportMessages([]);
    setShowNewChatDialog(false);
    setNewChatSearch('');
    // Fetch any existing messages
    fetchSupportMessages(user.id);
    // Add to conversations if not already there
    fetchSupportConversations();
  };

  const fetchAllUsersForChat = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getFilteredUsersForChat = () => {
    if (!newChatSearch) return allUsers.slice(0, 20);
    const search = newChatSearch.toLowerCase();
    return allUsers.filter(u => 
      u.name?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search)
    ).slice(0, 20);
  };

  // Quick message functions (from professionals/companies tabs)
  const openQuickMessageDialog = (user, userType) => {
    setQuickMessageUser({ ...user, user_type: userType });
    setQuickMessageContent('');
    setShowQuickMessageDialog(true);
  };

  const sendQuickMessage = async () => {
    if (!quickMessageContent.trim() || !quickMessageUser) return;
    
    setSendingQuickMessage(true);
    try {
      await axios.post(`${API}/admin/support-chat/send`, {
        receiver_id: quickMessageUser.id,
        content: quickMessageContent
      });
      setQuickMessageContent('');
      setShowQuickMessageDialog(false);
      setQuickMessageUser(null);
      toast.success(`Messaggio inviato a ${quickMessageUser.name}`);
      // Refresh support conversations if on that tab
      if (activeTab === 'support-chat') {
        fetchSupportConversations();
      }
    } catch (error) {
      console.error('Error sending quick message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSendingQuickMessage(false);
    }
  };

  const getFilteredProfessionals = () => {
    let filtered = [...professionals];
    
    // Filter by verification status
    if (verificationFilter === 'verified') {
      filtered = filtered.filter(p => p.is_verified);
    } else if (verificationFilter === 'unverified') {
      filtered = filtered.filter(p => !p.is_verified && !p.verification_requested);
    } else if (verificationFilter === 'requested') {
      filtered = filtered.filter(p => p.verification_requested);
    }
    
    // Filter by search
    if (verificationSearch) {
      const search = verificationSearch.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search)
      );
    }
    
    // Sort: requested first, then by date
    filtered.sort((a, b) => {
      // Prioritize verification requests
      if (a.verification_requested && !b.verification_requested) return -1;
      if (!a.verification_requested && b.verification_requested) return 1;
      return 0;
    });
    
    return filtered;
  };

  const documentOptions = [
    { value: 'piva', label: 'Partita IVA' },
    { value: 'identity', label: 'Documento di Identità' },
    { value: 'degree', label: 'Titolo di Studio' },
    { value: 'certification', label: 'Certificazione Professionale' },
    { value: 'insurance', label: 'Assicurazione Professionale' },
    { value: 'portfolio', label: 'Portfolio/Referenze' },
  ];
  
  // Sorting functions
  const toggleListingsSort = (column) => {
    if (listingsSortBy === column) {
      setListingsSortDir(listingsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setListingsSortBy(column);
      setListingsSortDir('desc');
    }
  };
  
  const toggleConversationsSort = (column) => {
    if (conversationsSortBy === column) {
      setConversationsSortDir(conversationsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setConversationsSortBy(column);
      setConversationsSortDir('desc');
    }
  };
  
  const toggleProfessionalsSort = (column) => {
    if (professionalsSortBy === column) {
      setProfessionalsSortDir(professionalsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setProfessionalsSortBy(column);
      setProfessionalsSortDir('desc');
    }
  };
  
  const toggleCompaniesSort = (column) => {
    if (companiesSortBy === column) {
      setCompaniesSortDir(companiesSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setCompaniesSortBy(column);
      setCompaniesSortDir('desc');
    }
  };
  
  const toggleReviewsSort = (column) => {
    if (reviewsSortBy === column) {
      setReviewsSortDir(reviewsSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setReviewsSortBy(column);
      setReviewsSortDir('desc');
    }
  };
  
  // Get sorted professionals
  const getSortedProfessionals = () => {
    let filtered = [...professionals];
    
    if (verificationSearch) {
      const search = verificationSearch.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search)
      );
    }
    
    if (verificationFilter === 'verified') {
      filtered = filtered.filter(p => p.is_verified);
    } else if (verificationFilter === 'unverified') {
      filtered = filtered.filter(p => !p.is_verified);
    }
    
    filtered.sort((a, b) => {
      let valA = a[professionalsSortBy];
      let valB = b[professionalsSortBy];
      
      if (professionalsSortBy === 'created_at') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      } else if (professionalsSortBy === 'average_rating') {
        valA = valA || 0;
        valB = valB || 0;
      } else if (typeof valA === 'string') {
        valA = valA?.toLowerCase() || '';
        valB = valB?.toLowerCase() || '';
      }
      
      if (professionalsSortDir === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    
    return filtered;
  };
  
  // Get sorted companies
  const getSortedCompanies = () => {
    let filtered = [...companies];
    
    if (companiesSearch) {
      const search = companiesSearch.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.sector?.toLowerCase().includes(search) ||
        c.vat_number?.toLowerCase().includes(search)
      );
    }
    
    filtered.sort((a, b) => {
      let valA = a[companiesSortBy];
      let valB = b[companiesSortBy];
      
      if (companiesSortBy === 'created_at') {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      } else if (companiesSortBy === 'listings_count' || companiesSortBy === 'reviews_given') {
        valA = valA || 0;
        valB = valB || 0;
      } else if (typeof valA === 'string') {
        valA = valA?.toLowerCase() || '';
        valB = valB?.toLowerCase() || '';
      }
      
      if (companiesSortDir === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    
    return filtered;
  };
  
  // View user details and load collaborations
  const viewUserDetail = async (user, type) => {
    setSelectedUserDetail({ ...user, userType: type });
    setUserCollaborations([]);
    setShowDetailDialog(true);
    
    // Load collaborations
    setLoadingCollaborations(true);
    try {
      const response = await axios.get(`${API}/admin/collaborations/${user.id}?user_type=${type}`);
      setUserCollaborations(response.data.collaborations || []);
    } catch (error) {
      console.error('Error loading collaborations:', error);
    } finally {
      setLoadingCollaborations(false);
    }
  };
  
  // Get sorted and filtered listings
  const getSortedListings = () => {
    let filtered = [...listings];
    
    // Filter by search
    if (listingsFilter.search) {
      const search = listingsFilter.search.toLowerCase();
      filtered = filtered.filter(l => 
        l.title?.toLowerCase().includes(search) ||
        l.user_name?.toLowerCase().includes(search) ||
        l.category?.toLowerCase().includes(search) ||
        l.sector?.toLowerCase().includes(search)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let valA = a[listingsSortBy];
      let valB = b[listingsSortBy];
      
      if (listingsSortBy === 'created_at') {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      } else if (typeof valA === 'string') {
        valA = valA?.toLowerCase() || '';
        valB = valB?.toLowerCase() || '';
      }
      
      if (listingsSortDir === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    
    return filtered;
  };
  
  // Get sorted and filtered conversations
  const getSortedConversations = () => {
    let filtered = [...conversations];
    
    // Filter by search
    if (conversationsSearch) {
      const search = conversationsSearch.toLowerCase();
      filtered = filtered.filter(c => {
        const names = Object.values(c.participants || {}).join(' ').toLowerCase();
        return names.includes(search) || c.last_message?.toLowerCase().includes(search);
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      let valA, valB;
      
      if (conversationsSortBy === 'last_timestamp') {
        valA = new Date(a.last_timestamp).getTime();
        valB = new Date(b.last_timestamp).getTime();
      } else if (conversationsSortBy === 'message_count') {
        valA = a.message_count;
        valB = b.message_count;
      } else if (conversationsSortBy === 'participants') {
        valA = Object.values(a.participants || {}).join(' ').toLowerCase();
        valB = Object.values(b.participants || {}).join(' ').toLowerCase();
      }
      
      if (conversationsSortDir === 'asc') {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    
    return filtered;
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/users?user_type=${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utenti_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export completato');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#344B71]"></div>
      </div>
    );
  }

  const successRate = stats.total_listings > 0 
    ? Math.round((stats.closed_with_success / stats.total_listings) * 100) 
    : 0;

  // Tab navigation items
  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: BarChart3 },
    { id: 'visitors', label: 'Visitatori', icon: Activity },
    { id: 'professionals', label: 'Professionisti', icon: User },
    { id: 'companies', label: 'Aziende', icon: Building2 },
    { id: 'listings', label: 'Annunci', icon: Briefcase },
    { id: 'messages', label: 'Messaggi', icon: MessageSquare },
    { id: 'support-chat', label: 'Chat Supporto', icon: Headphones },
    { id: 'reviews', label: 'Recensioni', icon: Star },
    { id: 'verifications', label: 'Verifiche', icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#344B71] to-[#344B71] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-slate-200 text-sm">ConsultantHUB</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="max-w-7xl mx-auto mt-4">
          <div className="flex space-x-1 bg-white/10 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-[#344B71]'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
        {/* Time Period Filter */}
        <Card className="p-4 mb-6 bg-white rounded-xl">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#344B71]" />
              <span className="font-medium text-[#344B71]">Periodo:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={timePeriod === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod('all')}
                className={timePeriod === 'all' ? 'bg-[#344B71] hover:bg-[#344B71]' : 'border-slate-300'}
              >
                Tutto
              </Button>
              <Button
                variant={timePeriod === '7d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod('7d')}
                className={timePeriod === '7d' ? 'bg-[#344B71] hover:bg-[#344B71]' : 'border-slate-300'}
              >
                Ultimi 7 giorni
              </Button>
              <Button
                variant={timePeriod === '30d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod('30d')}
                className={timePeriod === '30d' ? 'bg-[#344B71] hover:bg-[#344B71]' : 'border-slate-300'}
              >
                Ultimo mese
              </Button>
              <Button
                variant={timePeriod === '90d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod('90d')}
                className={timePeriod === '90d' ? 'bg-[#344B71] hover:bg-[#344B71]' : 'border-slate-300'}
              >
                Ultimi 3 mesi
              </Button>
              <Button
                variant={timePeriod === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimePeriod('custom')}
                className={timePeriod === 'custom' ? 'bg-[#344B71] hover:bg-[#344B71]' : 'border-slate-300'}
              >
                <Filter className="w-4 h-4 mr-1" />
                Personalizzato
              </Button>
            </div>
            
            {showCustomDates && (
              <div className="flex items-end gap-3 ml-auto">
                <div>
                  <Label className="text-xs text-slate-600">Da</Label>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">A</Label>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button
                  onClick={fetchData}
                  size="sm"
                  className="bg-[#E8A007] hover:bg-[#E09612]"
                  disabled={!customStartDate || !customEndDate}
                >
                  Applica
                </Button>
              </div>
            )}
          </div>
          
          {/* Period indicator */}
          {stats?.period_start && (
            <div className="mt-3 pt-3 border-t text-sm text-slate-600">
              📊 Statistiche dal <strong>{new Date(stats.period_start).toLocaleDateString('it-IT')}</strong> al <strong>{new Date(stats.period_end).toLocaleDateString('it-IT')}</strong>
            </div>
          )}
        </Card>

        {/* Period Stats Summary */}
        {timePeriod !== 'all' && stats && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="p-4 bg-gradient-to-r from-[#344B71]/10 to-[#E8A007]/10 border-[#344B71]/20 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-sm text-slate-600">Nuovi utenti nel periodo</p>
                    <p className="text-2xl font-bold text-[#344B71]">{stats.new_users_period}</p>
                  </div>
                  <div className="h-10 w-px bg-slate-300"></div>
                  <div>
                    <p className="text-sm text-slate-600">Nuovi annunci nel periodo</p>
                    <p className="text-2xl font-bold text-[#E8A007]">{stats.new_listings_period}</p>
                  </div>
                </div>
                <TrendingUp className="w-10 h-10 text-[#344B71]" />
              </div>
            </Card>
          </motion.div>
        )}

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <Card className="p-6 bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-[#344B71]" />
              <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                +{stats.new_users_today} oggi
              </span>
            </div>
            <h3 className="text-3xl font-bold text-[#344B71]">{stats.total_users}</h3>
            <p className="text-slate-600 text-sm">Utenti Totali</p>
            <div className="mt-4 flex items-center space-x-4 text-sm">
              <span className="flex items-center text-slate-600">
                <User className="w-4 h-4 mr-1" />
                {stats.professionals} Prof.
              </span>
              <span className="flex items-center text-slate-600">
                <Building2 className="w-4 h-4 mr-1" />
                {stats.companies} Aziende
              </span>
            </div>
          </Card>

          <Card className="p-6 bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <Briefcase className="w-8 h-8 text-[#E8A007]" />
              <span className="text-xs font-medium text-[#344B71] bg-[#344B71]/10 px-2 py-1 rounded-full">
                {stats.active_listings} attivi
              </span>
            </div>
            <h3 className="text-3xl font-bold text-[#344B71]">{stats.total_listings}</h3>
            <p className="text-slate-600 text-sm">Annunci Totali</p>
            <div className="mt-4 flex items-center space-x-4 text-sm">
              <span className="flex items-center text-emerald-600">
                <CheckCircle className="w-4 h-4 mr-1" />
                {stats.closed_with_success} successo
              </span>
              <span className="flex items-center text-slate-600">
                <Clock className="w-4 h-4 mr-1" />
                {stats.closed_listings} chiusi
              </span>
            </div>
          </Card>

          <Card className="p-6 bg-white rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <MessageSquare className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-3xl font-bold text-[#344B71]">{stats.total_messages}</h3>
            <p className="text-slate-600 text-sm">Messaggi Scambiati</p>
            <div className="mt-4 text-sm text-slate-600">
              {stats.total_conversations} conversazioni attive
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-[#E8A007]" />
            </div>
            <h3 className="text-3xl font-bold text-[#344B71]">{successRate}%</h3>
            <p className="text-slate-600 text-sm">Tasso di Successo</p>
            <div className="mt-4 text-sm text-slate-600">
              Annunci chiusi con successo
            </div>
          </Card>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Priority Distribution */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-[#E8A007]" />
                Distribuzione Priorità Annunci
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-50 rounded-xl">
                  <p className="text-3xl font-bold text-red-600">{stats.listings_by_priority.alta || 0}</p>
                  <p className="text-sm text-red-700">Priorità Alta</p>
                </div>
                <div className="text-center p-4 bg-[#344B71]/10 rounded-xl">
                  <p className="text-3xl font-bold text-[#344B71]">{stats.listings_by_priority.media || 0}</p>
                  <p className="text-sm text-[#344B71]">Priorità Media</p>
                </div>
                <div className="text-center p-4 bg-slate-100 rounded-xl">
                  <p className="text-3xl font-bold text-slate-600">{stats.listings_by_priority.bassa || 0}</p>
                  <p className="text-sm text-slate-700">Priorità Bassa</p>
                </div>
              </div>
            </Card>

            {/* Top Categories */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-[#E8A007]" />
                Categorie Più Richieste
              </h2>
              <div className="space-y-3">
                {Object.entries(stats.listings_by_category)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-slate-700">{category}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#E8A007] rounded-full"
                            style={{ width: `${(count / stats.total_listings) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-[#344B71] w-8">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </Card>

            {/* Top Sectors */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-[#344B71]" />
                Settori Più Attivi
              </h2>
              <div className="space-y-3">
                {Object.entries(stats.listings_by_sector)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([sector, count]) => (
                    <div key={sector} className="flex items-center justify-between">
                      <span className="text-slate-700">{sector}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#344B71] rounded-full"
                            style={{ width: `${(count / stats.total_listings) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-[#344B71] w-8">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* New Users Summary */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4">Nuovi Utenti</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                  <span className="text-slate-700">Oggi</span>
                  <span className="font-bold text-emerald-700">{stats.new_users_today}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#344B71]/10 rounded-xl">
                  <span className="text-slate-700">Ultima settimana</span>
                  <span className="font-bold text-[#344B71]">{stats.new_users_week}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#E8A007]/10 rounded-xl">
                  <span className="text-slate-700">Ultimo mese</span>
                  <span className="font-bold text-[#E8A007]">{stats.new_users_month}</span>
                </div>
              </div>
            </Card>

            {/* Export Section */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4 flex items-center">
                <Download className="w-5 h-5 mr-2 text-[#344B71]" />
                Esporta Dati
              </h2>
              <div className="space-y-3">
                <Button
                  onClick={() => handleExport('professional')}
                  className="w-full bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
                >
                  <User className="w-4 h-4 mr-2" />
                  Esporta Professionisti
                </Button>
                <Button
                  onClick={() => handleExport('company')}
                  variant="outline"
                  className="w-full border-[#344B71] text-[#344B71] hover:bg-[#344B71] hover:text-white rounded-xl"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Esporta Aziende
                </Button>
                <Button
                  onClick={() => handleExport('')}
                  variant="outline"
                  className="w-full border-slate-300 rounded-xl"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Esporta Tutti
                </Button>
              </div>
            </Card>

            {/* Recent Activities */}
            <Card className="p-6 bg-white rounded-xl">
              <h2 className="text-lg font-semibold text-[#344B71] mb-4">Attività Recenti</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activities.length > 0 ? (
                  activities.map((activity) => (
                    <div key={activity.id} className="p-3 bg-slate-50 rounded-xl text-sm">
                      <p className="text-[#344B71] font-medium">{activity.user_name}</p>
                      <p className="text-slate-600">
                        {activity.type === 'listing_closed' 
                          ? `Ha chiuso l'annuncio "${activity.listing_title}"`
                          : activity.type}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">
                        {new Date(activity.timestamp).toLocaleString('it-IT')}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4">Nessuna attività recente</p>
                )}
              </div>
            </Card>
          </div>
        </div>
          </>
        )}

        {/* VISITORS TAB */}
        {activeTab === 'visitors' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-[#344B71]">Registro Visitatori</h2>
                <Select value={visitorDays.toString()} onValueChange={(v) => { setVisitorDays(parseInt(v)); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                    <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                    <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={fetchVisitorData}
                variant="outline"
                className="rounded-xl"
                disabled={loadingVisitors}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingVisitors ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
            </div>

            {loadingVisitors ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-[#344B71]" />
              </div>
            ) : visitorData ? (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4 bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#344B71]/10 rounded-full flex items-center justify-center">
                        <Eye className="w-5 h-5 text-[#344B71]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Visite Totali</p>
                        <p className="text-2xl font-bold text-[#344B71]">{visitorData.total_visits}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Visitatori Unici</p>
                        <p className="text-2xl font-bold text-green-600">{visitorData.unique_visitors}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E8A007]/10 rounded-full flex items-center justify-center">
                        <Activity className="w-5 h-5 text-[#E8A007]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Visite Oggi</p>
                        <p className="text-2xl font-bold text-[#E8A007]">{visitorData.visits_today}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Globe className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Pagine Visitate</p>
                        <p className="text-2xl font-bold text-purple-600">{visitorData.visits_by_page?.length || 0}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pages Most Visited */}
                  <Card className="p-6 bg-white">
                    <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                      <List className="w-5 h-5" />
                      Pagine Più Visitate
                    </h3>
                    <div className="space-y-3">
                      {visitorData.visits_by_page?.slice(0, 10).map((page, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 truncate flex-1">{page.page}</span>
                          <span className="text-sm font-medium text-[#344B71] bg-[#344B71]/10 px-2 py-0.5 rounded">
                            {page.count} visite
                          </span>
                        </div>
                      ))}
                      {(!visitorData.visits_by_page || visitorData.visits_by_page.length === 0) && (
                        <p className="text-slate-500 text-sm">Nessun dato disponibile</p>
                      )}
                    </div>
                  </Card>

                  {/* Devices */}
                  <Card className="p-6 bg-white">
                    <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                      <Monitor className="w-5 h-5" />
                      Dispositivi
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Desktop</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#344B71] rounded-full" 
                              style={{ width: `${visitorData.total_visits > 0 ? (visitorData.visits_by_device?.desktop / visitorData.total_visits * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{visitorData.visits_by_device?.desktop || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Mobile</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 rounded-full" 
                              style={{ width: `${visitorData.total_visits > 0 ? (visitorData.visits_by_device?.mobile / visitorData.total_visits * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{visitorData.visits_by_device?.mobile || 0}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Tablet className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Tablet</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#E8A007] rounded-full" 
                              style={{ width: `${visitorData.total_visits > 0 ? (visitorData.visits_by_device?.tablet / visitorData.total_visits * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{visitorData.visits_by_device?.tablet || 0}</span>
                        </div>
                      </div>
                    </div>

                    <h4 className="font-medium text-slate-700 mt-6 mb-3">Browser</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(visitorData.visits_by_browser || {}).map(([browser, count]) => (
                        <span key={browser} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                          {browser}: {count}
                        </span>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Visits by Day Chart */}
                <Card className="p-6 bg-white">
                  <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Visite per Giorno
                  </h3>
                  <div className="h-48 flex items-end gap-1">
                    {visitorData.visits_by_day?.map((day, idx) => {
                      const maxCount = Math.max(...visitorData.visits_by_day.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div 
                          key={idx} 
                          className="flex-1 flex flex-col items-center group"
                          title={`${day.date}: ${day.count} visite`}
                        >
                          <div 
                            className="w-full bg-[#344B71] rounded-t transition-all hover:bg-[#E8A007]"
                            style={{ height: `${Math.max(height, 2)}%` }}
                          />
                          <span className="text-[8px] text-slate-400 mt-1 rotate-45 origin-left hidden md:block">
                            {day.date?.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Recent Visits Table */}
                <Card className="bg-white rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-200">
                    <h3 className="font-semibold text-[#344B71]">Ultime Visite</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Data/Ora</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Pagina</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Dispositivo</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Browser</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-600">Referrer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitorData.recent_visits?.slice(0, 20).map((visit, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-4 text-sm text-slate-600">{visit.formatted_time}</td>
                            <td className="p-4">
                              <span className="text-sm font-medium text-[#344B71]">{visit.page}</span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs ${
                                visit.device_type === 'mobile' ? 'bg-green-100 text-green-700' :
                                visit.device_type === 'tablet' ? 'bg-[#E8A007]/20 text-[#E8A007]' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {visit.device_type === 'mobile' ? '📱 Mobile' :
                                 visit.device_type === 'tablet' ? '📱 Tablet' : '💻 Desktop'}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-slate-600">{visit.browser}</td>
                            <td className="p-4 text-sm text-slate-500 truncate max-w-[200px]">
                              {visit.referrer || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="p-12 bg-white text-center">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nessun dato di visite disponibile</p>
                <p className="text-sm text-slate-400 mt-2">I dati inizieranno ad apparire quando i visitatori navigheranno sul sito</p>
              </Card>
            )}
          </div>
        )}

        {/* PROFESSIONALS TAB */}
        {activeTab === 'professionals' && (
          <div className="space-y-6">
            {/* Header with Search and Export */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cerca professionista..."
                    value={verificationSearch}
                    onChange={(e) => setVerificationSearch(e.target.value)}
                    className="pl-10 w-64 rounded-xl"
                  />
                </div>
                <span className="text-sm text-slate-500">
                  {professionals.length} professionisti totali
                </span>
              </div>
              <Button
                onClick={() => window.open(`${API}/admin/export/professionals`, '_blank')}
                variant="outline"
                className="rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta CSV
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Totale</p>
                <p className="text-2xl font-bold text-[#344B71]">{professionals.length}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Verificati</p>
                <p className="text-2xl font-bold text-green-600">{professionals.filter(p => p.is_verified).length}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Con Recensioni</p>
                <p className="text-2xl font-bold text-[#E8A007]">{professionals.filter(p => p.total_reviews > 0).length}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Iscritti Albo</p>
                <p className="text-2xl font-bold text-[#344B71]">{professionals.filter(p => p.iscrizione_albo).length}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Tot. Messaggi Inviati</p>
                <p className="text-2xl font-bold text-slate-700">{professionals.reduce((sum, p) => sum + (p.messages_sent || 0), 0)}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Tot. Collaborazioni</p>
                <p className="text-2xl font-bold text-purple-600">{professionals.reduce((sum, p) => sum + (p.collaborations_count || 0), 0)}</p>
              </Card>
            </div>

            {/* Professionals Table */}
            <Card className="bg-white rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleProfessionalsSort('name')}
                      >
                        <span className="flex items-center gap-1">
                          Nome
                          <SortIcon column="name" currentSort={professionalsSortBy} currentDir={professionalsSortDir} />
                        </span>
                      </th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleProfessionalsSort('category')}
                      >
                        <span className="flex items-center gap-1">
                          Categoria
                          <SortIcon column="category" currentSort={professionalsSortBy} currentDir={professionalsSortDir} />
                        </span>
                      </th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleProfessionalsSort('average_rating')}
                      >
                        <span className="flex items-center gap-1">
                          Rating
                          <SortIcon column="average_rating" currentSort={professionalsSortBy} currentDir={professionalsSortDir} />
                        </span>
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Aziende Contattate</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Collaborazioni</th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleProfessionalsSort('created_at')}
                      >
                        <span className="flex items-center gap-1">
                          Registrato il
                          <SortIcon column="created_at" currentSort={professionalsSortBy} currentDir={professionalsSortDir} />
                        </span>
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Stato</th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedProfessionals()
                      .map((pro) => (
                        <tr key={pro.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#344B71]/10 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-[#344B71]" />
                              </div>
                              <div>
                                <button 
                                  onClick={() => viewUserDetail(pro, 'professional')}
                                  className="font-medium text-[#344B71] block hover:underline text-left"
                                >
                                  {pro.name}
                                </button>
                                <span className="text-xs text-slate-500">{pro.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-slate-600">{pro.category || '-'}</span>
                            {pro.iscrizione_albo && (
                              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Albo</span>
                            )}
                          </td>
                          <td className="p-4">
                            {pro.total_reviews > 0 ? (
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4 fill-[#E8A007] text-[#E8A007]" />
                                <span className="font-medium">{pro.average_rating}</span>
                                <span className="text-xs text-slate-400">({pro.total_reviews})</span>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-xs font-medium">
                                {pro.companies_contacted || 0}
                              </span>
                              <span className="text-xs text-slate-400">
                                ({pro.messages_sent || 0} msg)
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                              {pro.collaborations_count || 0}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-slate-600">
                              {pro.created_at ? new Date(pro.created_at).toLocaleDateString('it-IT') : '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            {pro.is_verified ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1 w-fit">
                                <CheckCircle className="w-3 h-3" />
                                Verificato
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs w-fit">
                                Non verificato
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openQuickMessageDialog(pro, 'professional')}
                                className="text-[#344B71] hover:text-[#344B71] hover:bg-[#344B71]/10"
                                title="Invia messaggio"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(pro.id, pro.name)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Elimina utente"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {professionals.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nessun professionista registrato
                </div>
              )}
            </Card>
          </div>
        )}

        {/* COMPANIES TAB */}
        {activeTab === 'companies' && (
          <div className="space-y-6">
            {/* Header with Search and Export */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cerca azienda..."
                    value={companiesSearch}
                    onChange={(e) => setCompaniesSearch(e.target.value)}
                    className="pl-10 w-64 rounded-xl"
                  />
                </div>
                <span className="text-sm text-slate-500">
                  {companies.length} aziende totali
                </span>
              </div>
              <Button
                onClick={() => window.open(`${API}/admin/export/companies`, '_blank')}
                variant="outline"
                className="rounded-xl"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta CSV
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Totale</p>
                <p className="text-2xl font-bold text-[#344B71]">{companies.length}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Annunci Totali</p>
                <p className="text-2xl font-bold text-[#344B71]">{companies.reduce((sum, c) => sum + (c.listings_total || 0), 0)}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Annunci Attivi</p>
                <p className="text-2xl font-bold text-green-600">{companies.reduce((sum, c) => sum + (c.listings_active || 0), 0)}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Chiusi con Successo</p>
                <p className="text-2xl font-bold text-purple-600">{companies.reduce((sum, c) => sum + (c.listings_closed_success || 0), 0)}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Recensioni Date</p>
                <p className="text-2xl font-bold text-[#E8A007]">{companies.reduce((sum, c) => sum + (c.reviews_given || 0), 0)}</p>
              </Card>
              <Card className="p-4 bg-white">
                <p className="text-xs text-slate-500">Tot. Conversazioni</p>
                <p className="text-2xl font-bold text-slate-700">{companies.reduce((sum, c) => sum + (c.conversations_count || 0), 0)}</p>
              </Card>
            </div>

            {/* Companies Table */}
            <Card className="bg-white rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleCompaniesSort('name')}
                      >
                        <span className="flex items-center gap-1">
                          Azienda
                          <SortIcon column="name" currentSort={companiesSortBy} currentDir={companiesSortDir} />
                        </span>
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">P.IVA</th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleCompaniesSort('sector')}
                      >
                        <span className="flex items-center gap-1">
                          Settore
                          <SortIcon column="sector" currentSort={companiesSortBy} currentDir={companiesSortDir} />
                        </span>
                      </th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleCompaniesSort('listings_count')}
                      >
                        <span className="flex items-center gap-1">
                          Annunci
                          <SortIcon column="listings_count" currentSort={companiesSortBy} currentDir={companiesSortDir} />
                        </span>
                      </th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Chiusi/Successo</th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleCompaniesSort('created_at')}
                      >
                        <span className="flex items-center gap-1">
                          Registrata il
                          <SortIcon column="created_at" currentSort={companiesSortBy} currentDir={companiesSortDir} />
                        </span>
                      </th>
                      <th 
                        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleCompaniesSort('reviews_given')}
                      >
                        <span className="flex items-center gap-1">
                          Recensioni
                          <SortIcon column="reviews_given" currentSort={companiesSortBy} currentDir={companiesSortDir} />
                        </span>
                      </th>
                      <th className="text-center p-4 text-sm font-medium text-slate-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedCompanies()
                      .map((company) => (
                        <tr key={company.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-[#E8A007]/10 rounded-full flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-[#E8A007]" />
                              </div>
                              <div>
                                <button 
                                  onClick={() => viewUserDetail(company, 'company')}
                                  className="font-medium text-[#344B71] block hover:underline text-left"
                                >
                                  {company.company_name || company.name}
                                </button>
                                <span className="text-xs text-slate-500">{company.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600 font-mono">{company.partita_iva || '-'}</td>
                          <td className="p-4 text-sm text-slate-600">{company.sector || '-'}</td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-[#344B71]/10 text-[#344B71] rounded text-xs font-medium">
                                  {company.listings_total || 0} totali
                                </span>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                  {company.listings_active || 0} attivi
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                                {company.listings_closed || 0}
                              </span>
                              <span className="text-slate-400">/</span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                {company.listings_closed_success || 0} ✓
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-slate-600">
                              {company.created_at ? new Date(company.created_at).toLocaleDateString('it-IT') : '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 bg-[#E8A007]/10 text-[#E8A007] rounded-full text-xs font-medium">
                              {company.reviews_given || 0}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openQuickMessageDialog(company, 'company')}
                                className="text-[#344B71] hover:text-[#344B71] hover:bg-[#344B71]/10"
                                title="Invia messaggio"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(company.id, company.company_name || company.name)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Elimina utente"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {companies.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nessuna azienda registrata
                </div>
              )}
            </Card>
          </div>
        )}

        {/* LISTINGS TAB */}
        {activeTab === 'listings' && (
          <div className="space-y-6">
            {/* Filters */}
            <Card className="p-4 bg-white rounded-xl">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#344B71]" />
                  <span className="font-medium text-[#344B71]">Filtri:</span>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cerca per titolo, utente, categoria..."
                    value={listingsFilter.search}
                    onChange={(e) => setListingsFilter(f => ({...f, search: e.target.value}))}
                    className="pl-9 w-64"
                  />
                </div>
                <Select value={listingsFilter.status} onValueChange={(v) => { setListingsFilter(f => ({...f, status: v})); fetchListings(); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="active">Attivi</SelectItem>
                    <SelectItem value="closed">Chiusi</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={listingsFilter.priority} onValueChange={(v) => { setListingsFilter(f => ({...f, priority: v})); fetchListings(); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Priorità" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le priorità</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="bassa">Bassa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>
            
            {/* Listings Table */}
            <Card className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-[#344B71] flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-[#344B71]" />
                  Tutti gli Annunci ({getSortedListings().length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('title')}
                      >
                        <div className="flex items-center">
                          Titolo
                          <SortIcon column="title" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('user_name')}
                      >
                        <div className="flex items-center">
                          Utente
                          <SortIcon column="user_name" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('category')}
                      >
                        <div className="flex items-center">
                          Categoria
                          <SortIcon column="category" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('priority')}
                      >
                        <div className="flex items-center">
                          Priorità
                          <SortIcon column="priority" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('status')}
                      >
                        <div className="flex items-center">
                          Stato
                          <SortIcon column="status" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleListingsSort('created_at')}
                      >
                        <div className="flex items-center">
                          Data
                          <SortIcon column="created_at" currentSort={listingsSortBy} currentDir={listingsSortDir} />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {getSortedListings().map((listing) => (
                      <tr key={listing.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#344B71] text-sm truncate max-w-xs">{listing.title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-600">{listing.user_name}</p>
                          <p className="text-xs text-slate-400">{listing.user_type === 'professional' ? 'Professionista' : 'Azienda'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-700">{listing.category}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            listing.priority === 'alta' ? 'bg-red-100 text-red-700' :
                            listing.priority === 'media' ? 'bg-[#344B71]/10 text-[#344B71]' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {listing.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            listing.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {listing.status === 'active' ? 'Attivo' : 'Chiuso'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-slate-500">
                            {new Date(listing.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/listings/${listing.id}`, '_blank')}
                            className="text-[#344B71] hover:text-[#344B71]"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {getSortedListings().length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    Nessun annuncio trovato con i filtri selezionati
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            {/* Search and Sort */}
            <Card className="p-4 bg-white rounded-xl">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#344B71]" />
                  <span className="font-medium text-[#344B71]">Cerca:</span>
                </div>
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cerca per nome utente o contenuto..."
                    value={conversationsSearch}
                    onChange={(e) => setConversationsSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Ordina per:</span>
                  <Button
                    variant={conversationsSortBy === 'last_timestamp' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleConversationsSort('last_timestamp')}
                    className={conversationsSortBy === 'last_timestamp' ? 'bg-[#344B71]' : ''}
                  >
                    Data
                    {conversationsSortBy === 'last_timestamp' && (
                      conversationsSortDir === 'desc' ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
                    )}
                  </Button>
                  <Button
                    variant={conversationsSortBy === 'message_count' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleConversationsSort('message_count')}
                    className={conversationsSortBy === 'message_count' ? 'bg-[#344B71]' : ''}
                  >
                    N° Messaggi
                    {conversationsSortBy === 'message_count' && (
                      conversationsSortDir === 'desc' ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
                    )}
                  </Button>
                  <Button
                    variant={conversationsSortBy === 'participants' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleConversationsSort('participants')}
                    className={conversationsSortBy === 'participants' ? 'bg-[#344B71]' : ''}
                  >
                    Nome
                    {conversationsSortBy === 'participants' && (
                      conversationsSortDir === 'desc' ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
                    )}
                  </Button>
                </div>
              </div>
            </Card>
            
            <Card className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-[#344B71] flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-[#344B71]" />
                  Tutte le Conversazioni ({getSortedConversations().length})
                </h2>
              </div>
              <div className="divide-y divide-slate-200">
                {getSortedConversations().map((conv) => (
                  <div 
                    key={conv.id} 
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => viewConversationMessages(conv.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          {Object.values(conv.participants).map((name, idx) => (
                            <span key={idx} className="text-sm font-medium text-[#344B71]">
                              {name}{idx < Object.values(conv.participants).length - 1 ? ' ↔ ' : ''}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-slate-600 truncate max-w-lg">{conv.last_message}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {conv.message_count} messaggi • {new Date(conv.last_timestamp).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
                {getSortedConversations().length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    Nessuna conversazione trovata
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* SUPPORT CHAT TAB */}
        {activeTab === 'support-chat' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-[#344B71] flex items-center gap-2">
                  <Headphones className="w-6 h-6" />
                  Chat Supporto
                </h2>
                {supportUnreadCount > 0 && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    {supportUnreadCount} non letti
                  </span>
                )}
              </div>
              <Button
                onClick={() => {
                  setShowNewChatDialog(true);
                  fetchAllUsersForChat();
                }}
                className="bg-[#344B71] hover:bg-[#344B71]/90 rounded-xl"
              >
                <Send className="w-4 h-4 mr-2" />
                Nuova Conversazione
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: '70vh' }}>
              {/* Conversations List */}
              <Card className="bg-white rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-semibold text-[#344B71]">Conversazioni</h3>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 60px)' }}>
                  {supportConversations.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <Headphones className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Nessuna conversazione attiva</p>
                      <p className="text-sm mt-2">Clicca "Nuova Conversazione" per iniziare</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {supportConversations.map((conv) => (
                        <div
                          key={conv.id}
                          onClick={() => fetchSupportMessages(conv.user_id)}
                          className={`p-4 cursor-pointer transition-all ${
                            selectedSupportUser?.id === conv.user_id
                              ? 'bg-[#344B71] text-white'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                              selectedSupportUser?.id === conv.user_id
                                ? 'bg-white text-[#344B71]'
                                : conv.user_type === 'professional'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {conv.user_type === 'professional' ? (
                                <User className="w-5 h-5" />
                              ) : (
                                <Building2 className="w-5 h-5" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">{conv.user_name}</p>
                                {conv.unread_count > 0 && selectedSupportUser?.id !== conv.user_id && (
                                  <span className="px-2 py-0.5 bg-red-500 text-white rounded-full text-xs">
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs truncate ${
                                selectedSupportUser?.id === conv.user_id ? 'text-slate-200' : 'text-slate-500'
                              }`}>
                                {conv.user_email}
                              </p>
                              <p className={`text-xs truncate mt-1 ${
                                selectedSupportUser?.id === conv.user_id ? 'text-slate-300' : 'text-slate-400'
                              }`}>
                                {conv.last_sender === 'admin' ? '↩ Tu: ' : ''}{conv.last_message?.slice(0, 40)}...
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Chat Area */}
              <Card className="lg:col-span-2 bg-white rounded-xl overflow-hidden flex flex-col">
                {selectedSupportUser ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-[#344B71] to-[#344B71] text-white">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedSupportUser.user_type === 'professional'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {selectedSupportUser.user_type === 'professional' ? (
                            <User className="w-5 h-5" />
                          ) : (
                            <Building2 className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{selectedSupportUser.name}</p>
                          <p className="text-sm text-slate-200">{selectedSupportUser.email}</p>
                        </div>
                        <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                          selectedSupportUser.user_type === 'professional'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {selectedSupportUser.user_type === 'professional' ? 'Professionista' : 'Azienda'}
                        </span>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" style={{ maxHeight: 'calc(70vh - 180px)' }}>
                      {supportMessages.length === 0 ? (
                        <div className="text-center text-slate-500 py-12">
                          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>Nessun messaggio ancora</p>
                          <p className="text-sm mt-1">Inizia la conversazione scrivendo un messaggio</p>
                        </div>
                      ) : (
                        supportMessages.map((msg) => {
                          const isAdmin = msg.sender_id === 'consultanthub-support';
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[70%] rounded-2xl p-4 ${
                                isAdmin
                                  ? 'bg-[#344B71] text-white'
                                  : 'bg-white border border-slate-200 text-slate-800'
                              }`}>
                                {!isAdmin && (
                                  <p className="text-xs font-medium text-slate-500 mb-1">{msg.sender_name}</p>
                                )}
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                <p className={`text-xs mt-2 ${isAdmin ? 'text-slate-300' : 'text-slate-400'}`}>
                                  {new Date(msg.timestamp).toLocaleString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={supportMessagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="flex gap-2">
                        <Input
                          value={newSupportMessage}
                          onChange={(e) => setNewSupportMessage(e.target.value)}
                          placeholder="Scrivi un messaggio di servizio..."
                          className="flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendSupportMessage();
                            }
                          }}
                        />
                        <Button
                          onClick={sendSupportMessage}
                          disabled={!newSupportMessage.trim() || sendingSupportMessage}
                          className="bg-[#344B71] hover:bg-[#344B71]/90"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <Headphones className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">Seleziona una conversazione</p>
                      <p className="text-sm mt-2">O inizia una nuova chat con un utente</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* New Chat Dialog */}
            <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
              <DialogContent className="max-w-md bg-white">
                <DialogHeader>
                  <DialogTitle className="text-[#344B71]">Nuova Conversazione</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Cerca utente per nome o email..."
                      value={newChatSearch}
                      onChange={(e) => setNewChatSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {getFilteredUsersForChat().map((u) => (
                      <div
                        key={u.id}
                        onClick={() => startNewSupportChat(u)}
                        className="p-3 rounded-xl border border-slate-200 hover:border-[#344B71] hover:bg-slate-50 cursor-pointer transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            u.user_type === 'professional'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {u.user_type === 'professional' ? (
                              <User className="w-4 h-4" />
                            ) : (
                              <Building2 className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-[#344B71]">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                          <span className={`ml-auto px-2 py-0.5 rounded text-xs ${
                            u.user_type === 'professional'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {u.user_type === 'professional' ? 'Prof.' : 'Azienda'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {allUsers.length === 0 && (
                      <p className="text-center text-slate-500 py-4">Caricamento utenti...</p>
                    )}
                    {allUsers.length > 0 && getFilteredUsersForChat().length === 0 && (
                      <p className="text-center text-slate-500 py-4">Nessun utente trovato</p>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Reviews Statistics */}
            {reviewsData.statistics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-6 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#E8A007]/10 rounded-xl flex items-center justify-center">
                      <Star className="w-6 h-6 text-[#E8A007]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Recensioni Totali</p>
                      <p className="text-2xl font-bold text-[#344B71]">{reviewsData.statistics.total_reviews}</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Tasso Conferma</p>
                      <p className="text-2xl font-bold text-[#344B71]">{reviewsData.statistics.confirmation_rate}%</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#344B71]/10 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#344B71]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Rating Medio</p>
                      <p className="text-2xl font-bold text-[#344B71]">{reviewsData.statistics.average_rating.toFixed(1)}</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Con Conferma</p>
                      <p className="text-2xl font-bold text-[#344B71]">{reviewsData.statistics.reviews_with_confirmation}</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Rating Distribution */}
            {reviewsData.statistics && (
              <Card className="p-6 bg-white">
                <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Distribuzione Valutazioni
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = reviewsData.statistics.rating_distribution[star] || 0;
                      const total = reviewsData.statistics.total_reviews || 1;
                      const percentage = Math.round((count / total) * 100);
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="w-4 text-sm text-slate-600">{star}</span>
                          <Star className="w-4 h-4 fill-[#E8A007] text-[#E8A007]" />
                          <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#E8A007] rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="w-12 text-sm text-slate-500 text-right">{count} ({percentage}%)</span>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <h4 className="font-medium text-[#344B71] mb-3">Per Tipo di Collaborazione</h4>
                    <div className="space-y-2">
                      {Object.entries(reviewsData.statistics.by_collaboration_type || {}).map(([type, count]) => (
                        <div key={type} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-600 capitalize">
                            {type === 'consulting' ? 'Consulenza' :
                             type === 'project' ? 'Progetto' :
                             type === 'audit' ? 'Audit' :
                             type === 'training' ? 'Formazione' :
                             type === 'non_specificato' ? 'Non specificato' : type}
                          </span>
                          <span className="font-medium text-[#344B71]">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Reviews List */}
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Ultime Recensioni
              </h3>
              <div className="space-y-4">
                {reviewsData.reviews.map((review) => (
                  <div key={review.id} className="p-4 border border-slate-200 rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-[#344B71]">{review.reviewer_name}</p>
                        <p className="text-xs text-slate-500">{review.reviewer_company}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#E8A007] text-[#E8A007]' : 'text-slate-200'}`} />
                          ))}
                        </div>
                        {review.collaboration_confirmed && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Confermato
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs text-slate-500 mb-2">
                      <span>Professionista: <strong>{review.professional_name}</strong></span>
                      <span>{new Date(review.created_at).toLocaleDateString('it-IT')}</span>
                      {review.collaboration_type && (
                        <span className="px-2 py-0.5 bg-slate-100 rounded-full capitalize">
                          {review.collaboration_type === 'consulting' ? 'Consulenza' :
                           review.collaboration_type === 'project' ? 'Progetto' :
                           review.collaboration_type === 'audit' ? 'Audit' :
                           review.collaboration_type === 'training' ? 'Formazione' : review.collaboration_type}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-slate-800">{review.title}</p>
                    <p className="text-sm text-slate-600 mt-1">{review.comment}</p>
                  </div>
                ))}
                {reviewsData.reviews.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    Nessuna recensione ancora
                  </div>
                )}
              </div>
            </Card>

            {/* Reviews Relationships Summary */}
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Riepilogo Rapporti Aziende-Professionisti
              </h3>
              {reviewsRelationships.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  Nessun rapporto ancora registrato
                </div>
              ) : (
                <div className="space-y-6">
                  {reviewsRelationships.map((company) => (
                    <div key={company.company_id} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 p-4 border-b border-slate-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#344B71]/10 rounded-xl flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-[#344B71]" />
                            </div>
                            <div>
                              <p className="font-semibold text-[#344B71]">{company.company_company_name || company.company_name}</p>
                              <p className="text-sm text-slate-500">{company.company_name}</p>
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-sm font-medium">
                            {company.total_professionals_reviewed} professionista/i recensito/i
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {company.professionals.map((pro) => (
                            <div key={pro.professional_id} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-medium text-[#344B71]">{pro.professional_name}</p>
                                  <p className="text-xs text-slate-500">{pro.category}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Star className="w-4 h-4 fill-[#E8A007] text-[#E8A007]" />
                                  <span className="font-medium text-[#344B71]">{pro.average_rating}</span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-slate-500">
                                  {pro.total_reviews} recensione/i
                                </span>
                                {pro.collaboration_types.map((type, idx) => (
                                  <span key={idx} className="text-xs px-2 py-0.5 bg-white rounded-full border border-slate-200">
                                    {type === 'consulting' ? 'Consulenza' :
                                     type === 'project' ? 'Progetto' :
                                     type === 'audit' ? 'Audit' :
                                     type === 'training' ? 'Formazione' : type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* VERIFICATIONS TAB */}
        {activeTab === 'verifications' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#344B71]/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#344B71]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Totale Professionisti</p>
                    <p className="text-2xl font-bold text-[#344B71]">{professionals.length}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Verificati</p>
                    <p className="text-2xl font-bold text-[#344B71]">
                      {professionals.filter(p => p.is_verified).length}
                    </p>
                  </div>
                </div>
              </Card>
              
              {/* Pending Requests - Highlighted */}
              <Card className={`p-6 ${professionals.filter(p => p.verification_requested).length > 0 ? 'bg-orange-50 border-orange-200 border-2' : 'bg-white'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${professionals.filter(p => p.verification_requested).length > 0 ? 'bg-orange-200' : 'bg-orange-100'}`}>
                    <Send className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Richieste in Attesa</p>
                    <p className={`text-2xl font-bold ${professionals.filter(p => p.verification_requested).length > 0 ? 'text-orange-600' : 'text-[#344B71]'}`}>
                      {professionals.filter(p => p.verification_requested).length}
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Da Verificare</p>
                    <p className="text-2xl font-bold text-[#344B71]">
                      {professionals.filter(p => !p.is_verified && p.has_profile && !p.verification_requested).length}
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Senza Profilo</p>
                    <p className="text-2xl font-bold text-[#344B71]">
                      {professionals.filter(p => !p.has_profile).length}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Pending Requests Alert */}
            {professionals.filter(p => p.verification_requested).length > 0 && (
              <Card className="p-4 bg-orange-50 border-orange-200 border-2">
                <div className="flex items-center gap-3">
                  <Send className="w-5 h-5 text-orange-600" />
                  <span className="font-medium text-orange-800">
                    Hai {professionals.filter(p => p.verification_requested).length} richieste di verifica in attesa di revisione!
                  </span>
                  <Button
                    size="sm"
                    onClick={() => setVerificationFilter('requested')}
                    className="ml-auto bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Visualizza Richieste
                  </Button>
                </div>
              </Card>
            )}

            {/* Filters */}
            <Card className="p-4 bg-white">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-[#344B71]" />
                  <span className="font-medium text-[#344B71]">Filtra:</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={verificationFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setVerificationFilter('all')}
                    className={verificationFilter === 'all' ? 'bg-[#344B71]' : ''}
                  >
                    Tutti
                  </Button>
                  <Button
                    size="sm"
                    variant={verificationFilter === 'requested' ? 'default' : 'outline'}
                    onClick={() => setVerificationFilter('requested')}
                    className={verificationFilter === 'requested' ? 'bg-orange-600' : ''}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Richieste ({professionals.filter(p => p.verification_requested).length})
                  </Button>
                  <Button
                    size="sm"
                    variant={verificationFilter === 'verified' ? 'default' : 'outline'}
                    onClick={() => setVerificationFilter('verified')}
                    className={verificationFilter === 'verified' ? 'bg-green-600' : ''}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Verificati
                  </Button>
                  <Button
                    size="sm"
                    variant={verificationFilter === 'unverified' ? 'default' : 'outline'}
                    onClick={() => setVerificationFilter('unverified')}
                    className={verificationFilter === 'unverified' ? 'bg-amber-600' : ''}
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Da Verificare
                  </Button>
                </div>
                <div className="flex-1 max-w-xs">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca per nome, email..."
                      value={verificationSearch}
                      onChange={(e) => setVerificationSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Professionals List */}
            <Card className="p-6 bg-white">
              <h3 className="font-semibold text-[#344B71] mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Professionisti ({getFilteredProfessionals().length})
              </h3>
              <div className="space-y-3">
                {getFilteredProfessionals().map((pro) => (
                  <div key={pro.id} className={`p-4 border rounded-xl hover:bg-slate-50 transition-colors ${
                    pro.verification_requested ? 'border-orange-300 bg-orange-50/50' : 'border-slate-200'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                          pro.verification_requested 
                            ? 'bg-gradient-to-br from-orange-400 to-orange-600' 
                            : 'bg-gradient-to-br from-[#344B71] to-[#344B71]'
                        }`}>
                          {pro.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#344B71]">{pro.name}</p>
                            {pro.is_verified && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Verificato
                              </span>
                            )}
                            {pro.verification_requested && (
                              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs flex items-center gap-1">
                                <Send className="w-3 h-3" />
                                Richiesta Verifica
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{pro.email}</p>
                          <div className="flex gap-4 mt-1 text-xs text-slate-500">
                            {pro.category && <span>{pro.category}</span>}
                            {pro.experience_years && <span>{pro.experience_years} anni exp.</span>}
                            {pro.total_reviews > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-[#E8A007] text-[#E8A007]" />
                                {pro.average_rating} ({pro.total_reviews})
                              </span>
                            )}
                          </div>
                          {/* Show verification request message */}
                          {pro.verification_requested && pro.verification_request_message && (
                            <div className="mt-2 p-2 bg-orange-100 rounded-lg">
                              <p className="text-xs text-orange-700">
                                <strong>Messaggio:</strong> &quot;{pro.verification_request_message}&quot;
                              </p>
                              <p className="text-xs text-orange-500 mt-1">
                                Richiesta il {new Date(pro.verification_requested_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                          )}
                          {pro.is_verified && pro.verification_documents?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {pro.verification_documents.map((doc, i) => (
                                <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                  {documentOptions.find(d => d.value === doc)?.label || doc}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {pro.is_verified ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnverifyProfessional(pro.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Rimuovi Verifica
                          </Button>
                        ) : pro.has_profile ? (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedProfessional(pro);
                              setVerificationNotes(pro.verification_request_message || '');
                              setShowVerifyDialog(true);
                            }}
                            className={pro.verification_requested 
                              ? "bg-orange-600 hover:bg-orange-700 text-white" 
                              : "bg-green-600 hover:bg-green-700 text-white"}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {pro.verification_requested ? 'Approva Richiesta' : 'Verifica'}
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400 px-3 py-2">Profilo non completato</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {getFilteredProfessionals().length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    Nessun professionista trovato
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </main>

      {/* Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#344B71]">Verifica Professionista</DialogTitle>
          </DialogHeader>
          {selectedProfessional && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium text-[#344B71]">{selectedProfessional.name}</p>
                <p className="text-sm text-slate-500">{selectedProfessional.email}</p>
                {selectedProfessional.category && (
                  <p className="text-sm text-slate-500 mt-1">{selectedProfessional.category}</p>
                )}
              </div>
              
              <div>
                <Label className="text-slate-700">Documenti Verificati</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {documentOptions.map((doc) => (
                    <label key={doc.value} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={verificationDocs.includes(doc.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVerificationDocs([...verificationDocs, doc.value]);
                          } else {
                            setVerificationDocs(verificationDocs.filter(d => d !== doc.value));
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm">{doc.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700">Note (opzionale)</Label>
                <Input
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Aggiungi note sulla verifica..."
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowVerifyDialog(false);
                    setSelectedProfessional(null);
                    setVerificationDocs([]);
                    setVerificationNotes('');
                  }}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleVerifyProfessional}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Conferma Verifica
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={showMessagesDialog} onOpenChange={setShowMessagesDialog}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-[#344B71]">Conversazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto p-2">
            {conversationMessages.map((msg, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-xl">
                <div className="flex justify-between items-start mb-1">
                  <p className="font-medium text-[#344B71] text-sm">{msg.sender_name}</p>
                  <p className="text-xs text-slate-400">
                    {new Date(msg.timestamp).toLocaleString('it-IT')}
                  </p>
                </div>
                <p className="text-slate-700 text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-[#344B71]">
              {selectedUserDetail?.userType === 'professional' ? (
                <User className="w-6 h-6 text-[#344B71]" />
              ) : (
                <Building2 className="w-6 h-6 text-[#E8A007]" />
              )}
              Dettaglio {selectedUserDetail?.userType === 'professional' ? 'Professionista' : 'Azienda'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedUserDetail && (
            <div className="space-y-6 py-4 bg-white">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Nome</p>
                  <p className="font-medium text-[#344B71]">{selectedUserDetail.company_name || selectedUserDetail.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-slate-700">{selectedUserDetail.email}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Data Registrazione</p>
                  <p className="text-slate-700">
                    {selectedUserDetail.created_at 
                      ? new Date(selectedUserDetail.created_at).toLocaleString('it-IT') 
                      : '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">ID Utente</p>
                  <p className="text-slate-700 font-mono text-xs">{selectedUserDetail.id}</p>
                </div>
              </div>

              {/* Professional-specific fields */}
              {selectedUserDetail.userType === 'professional' && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Informazioni Professionali</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Categoria</p>
                        <p className="text-slate-700">{selectedUserDetail.category || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Titolo</p>
                        <p className="text-slate-700">{selectedUserDetail.professional_title || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Provincia</p>
                        <p className="text-slate-700">{selectedUserDetail.province || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Anni Esperienza</p>
                        <p className="text-slate-700">{selectedUserDetail.experience_years || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Tariffa Oraria</p>
                        <p className="text-slate-700">{selectedUserDetail.hourly_rate ? `€${selectedUserDetail.hourly_rate}/h` : '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Iscrizione Albo</p>
                        <p className="text-slate-700">{selectedUserDetail.iscrizione_albo ? 'Sì' : 'No'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Verificato</p>
                        <p className={selectedUserDetail.is_verified ? 'text-green-600 font-medium' : 'text-slate-500'}>
                          {selectedUserDetail.is_verified ? 'Sì ✓' : 'No'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Rating Medio</p>
                        <p className="text-slate-700 flex items-center gap-1">
                          {selectedUserDetail.average_rating ? (
                            <>
                              <Star className="w-4 h-4 fill-[#E8A007] text-[#E8A007]" />
                              {selectedUserDetail.average_rating} ({selectedUserDetail.total_reviews || 0} recensioni)
                            </>
                          ) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Statistiche Attività</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#344B71]">{selectedUserDetail.companies_contacted || 0}</p>
                        <p className="text-xs text-slate-500">Aziende Contattate</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#344B71]">{selectedUserDetail.messages_sent || 0}</p>
                        <p className="text-xs text-slate-500">Messaggi Inviati</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{selectedUserDetail.collaborations_count || 0}</p>
                        <p className="text-xs text-slate-500">Collaborazioni</p>
                      </div>
                    </div>
                  </div>

                  {/* Collaborations Detail */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Dettaglio Collaborazioni
                    </h4>
                    {loadingCollaborations ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : userCollaborations.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {userCollaborations.map((collab, idx) => (
                          <div key={idx} className="bg-slate-50 p-3 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-[#344B71]">{collab.partner_name}</p>
                                <p className="text-xs text-slate-500">{collab.partner_email}</p>
                                {collab.collaboration_type && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                    {collab.collaboration_type}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="flex items-center gap-1 text-[#E8A007]">
                                  <Star className="w-4 h-4 fill-current" />
                                  <span className="font-medium">{collab.rating}</span>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  {collab.date ? new Date(collab.date).toLocaleDateString('it-IT') : '-'}
                                </p>
                              </div>
                            </div>
                            {collab.comment && (
                              <p className="text-sm text-slate-600 mt-2 italic">"{collab.comment}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-2">Nessuna collaborazione registrata</p>
                    )}
                  </div>

                  {selectedUserDetail.bio && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-slate-900 mb-2">Bio</h4>
                      <p className="text-slate-600 text-sm whitespace-pre-wrap">{selectedUserDetail.bio}</p>
                    </div>
                  )}
                </>
              )}

              {/* Company-specific fields */}
              {selectedUserDetail.userType === 'company' && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Informazioni Azienda</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Ragione Sociale</p>
                        <p className="text-slate-700">{selectedUserDetail.company_name || selectedUserDetail.name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">P.IVA</p>
                        <p className="text-slate-700 font-mono">{selectedUserDetail.partita_iva || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Settore</p>
                        <p className="text-slate-700">{selectedUserDetail.sector || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Dimensione</p>
                        <p className="text-slate-700">{selectedUserDetail.company_size || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Provincia</p>
                        <p className="text-slate-700">{selectedUserDetail.province || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">Sito Web</p>
                        <p className="text-slate-700">
                          {selectedUserDetail.website ? (
                            <a href={selectedUserDetail.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {selectedUserDetail.website}
                            </a>
                          ) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Statistiche Annunci</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#344B71]">{selectedUserDetail.listings_total || 0}</p>
                        <p className="text-xs text-slate-500">Totali</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{selectedUserDetail.listings_active || 0}</p>
                        <p className="text-xs text-slate-500">Attivi</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-600">{selectedUserDetail.listings_closed || 0}</p>
                        <p className="text-xs text-slate-500">Chiusi</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{selectedUserDetail.listings_closed_success || 0}</p>
                        <p className="text-xs text-slate-500">Con Successo</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Attività</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#E8A007]/10 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-[#E8A007]">{selectedUserDetail.reviews_given || 0}</p>
                        <p className="text-xs text-slate-500">Recensioni Date</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-slate-600">{selectedUserDetail.conversations_count || 0}</p>
                        <p className="text-xs text-slate-500">Conversazioni</p>
                      </div>
                    </div>
                  </div>

                  {/* Collaborations Detail for Company */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Professionisti Collaborati
                    </h4>
                    {loadingCollaborations ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : userCollaborations.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {userCollaborations.map((collab, idx) => (
                          <div key={idx} className="bg-slate-50 p-3 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-[#344B71]">{collab.partner_name}</p>
                                <p className="text-xs text-slate-500">{collab.partner_email}</p>
                                {collab.partner_category && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-[#344B71]/10 text-[#344B71] rounded text-xs">
                                    {collab.partner_category}
                                  </span>
                                )}
                                {collab.collaboration_type && (
                                  <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                    {collab.collaboration_type}
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="flex items-center gap-1 text-[#E8A007]">
                                  <Star className="w-4 h-4 fill-current" />
                                  <span className="font-medium">{collab.rating}</span>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  {collab.date ? new Date(collab.date).toLocaleDateString('it-IT') : '-'}
                                </p>
                              </div>
                            </div>
                            {collab.comment && (
                              <p className="text-sm text-slate-600 mt-2 italic">"{collab.comment}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-2">Nessuna collaborazione registrata</p>
                    )}
                  </div>

                  {selectedUserDetail.description && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-slate-900 mb-2">Descrizione</h4>
                      <p className="text-slate-600 text-sm whitespace-pre-wrap">{selectedUserDetail.description}</p>
                    </div>
                  )}
                </>
              )}

              {/* Actions */}
              <div className="border-t pt-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Message Dialog */}
      <Dialog open={showQuickMessageDialog} onOpenChange={setShowQuickMessageDialog}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#344B71] flex items-center gap-2">
              <Send className="w-5 h-5" />
              Invia Messaggio
            </DialogTitle>
          </DialogHeader>
          {quickMessageUser && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  quickMessageUser.user_type === 'professional'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {quickMessageUser.user_type === 'professional' ? (
                    <User className="w-5 h-5" />
                  ) : (
                    <Building2 className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-[#344B71]">{quickMessageUser.name || quickMessageUser.company_name}</p>
                  <p className="text-sm text-slate-500">{quickMessageUser.email}</p>
                </div>
                <span className={`ml-auto px-2 py-1 rounded text-xs ${
                  quickMessageUser.user_type === 'professional'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {quickMessageUser.user_type === 'professional' ? 'Professionista' : 'Azienda'}
                </span>
              </div>

              {/* Message Input */}
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Messaggio</Label>
                <textarea
                  value={quickMessageContent}
                  onChange={(e) => setQuickMessageContent(e.target.value)}
                  placeholder="Scrivi il tuo messaggio di servizio..."
                  className="w-full h-32 p-3 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#344B71]/20 focus:border-[#344B71]"
                />
              </div>

              {/* Info */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl text-sm">
                <Headphones className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-blue-700">
                  Il messaggio apparirà nella sezione "Messaggi" dell'utente come comunicazione dal supporto ConsultantHUB.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowQuickMessageDialog(false);
                    setQuickMessageUser(null);
                    setQuickMessageContent('');
                  }}
                >
                  Annulla
                </Button>
                <Button
                  onClick={sendQuickMessage}
                  disabled={!quickMessageContent.trim() || sendingQuickMessage}
                  className="bg-[#344B71] hover:bg-[#344B71]/90"
                >
                  {sendingQuickMessage ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Invia Messaggio
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
